import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import {
  type AppContext,
  SessionError,
  type CheckoutSession,
  type SendMobileMessageFunc,
} from "@/types";
import Button from "@/shared/ui/components/button";
import { ErrorIcon } from "@/shared/ui/icons/error-icon";
import * as events from "@/events";
import { emitErrorTracking } from "@/shared/tracking/analytics";
import i18n from "@/i18n";
import enUSErrors from "@/locale/en_US.errors.json";
import arSAErrors from "@/locale/ar_SA.errors.json";
import { logInfo } from "@/services/logging/logger";
import { wait } from "@/lib/common";

interface ErrorScreenProps {
  ctx: AppContext;
}

interface FullscreenErrorContent {
  title: string;
  message: string;
  cta?: string;
}

interface ErrorLocale {
  fullscreen: Record<string, FullscreenErrorContent>;
}

const errorLocales: Record<string, ErrorLocale> = {
  en_US: enUSErrors as ErrorLocale,
  ar_SA: arSAErrors as ErrorLocale,
};

function getErrorContent(code: string | undefined): FullscreenErrorContent {
  const language = i18n.language?.startsWith("ar") ? "ar_SA" : "en_US";
  const locale = errorLocales[language] ?? errorLocales.en_US;
  const fullscreenErrors = locale.fullscreen;

  if (code && fullscreenErrors[code]) {
    return fullscreenErrors[code];
  }

  return fullscreenErrors.UNKNOWN;
}

interface LegacyErrorConfig {
  title: string;
  message: string;
}

const LEGACY_ERRORS = new Map<SessionError, LegacyErrorConfig>([
  [
    SessionError.UNKNOWN,
    {
      title: "error.unknown.title",
      message: "error.unknown.message",
    },
  ],
  [
    SessionError.NOT_FOUND,
    {
      title: "error.session_not_found.title",
      message: "error.session_not_found.message",
    },
  ],
]);

const isTranslationKey = (str: string): boolean => str.startsWith("error.");

const onGoBackToMerchantClicked =
  (
    session: CheckoutSession,
    redirecting: boolean,
    setRedirecting: Dispatch<SetStateAction<boolean>>,
    sendMobileMessage: SendMobileMessageFunc,
  ) =>
  async (): Promise<void> => {
    if (redirecting) {
      return;
    }

    setRedirecting(true);

    const redirectTo = session.merchant.urls.failure;

    try {
      logInfo(
        "error-screen",
        `Redirecting to Merchant's Failure URL: ${redirectTo}`,
        {
          checkoutId: session.id,
          status: session.status,
        },
      );

      sendMobileMessage("instore_checkout_failure", "back");

      emitErrorTracking("go_back_to_merchant_clicked", session, {
        error_type: session.error?.type ?? SessionError.UNKNOWN,
      });
    } catch (err) {
      console.error(err);
    }

    // Artificial delay gives enough time for the request to be sent,
    // otherwise the redirection happens too immediately.
    await wait(2000);

    window.location.href = redirectTo;
  };

export function ErrorScreen({ ctx }: ErrorScreenProps): React.JSX.Element {
  const { t, session, sendEvent, sendMobileMessage } = ctx;

  const errorCode = session.error?.code;
  const errorContent = getErrorContent(errorCode);
  const legacyConfig = LEGACY_ERRORS.get(
    session.error?.type ?? SessionError.UNKNOWN,
  );

  const title =
    (errorCode && errorContent.title) ||
    legacyConfig?.title ||
    "error.unknown.title";

  const message =
    (errorCode && errorContent.message) ||
    legacyConfig?.message ||
    "error.unknown.message";

  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    const errorData = {
      error_type: session.error?.type ?? SessionError.UNKNOWN,
      error_code: errorCode ?? "",
      error_message: session.error?.message ?? "",
    };

    sendEvent(events.ERROR_SCREEN_SHOWN, errorData);

    emitErrorTracking("error_screen_shown", session, errorData);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-white flex items-center justify-center px-4 flex-grow mt-50">
      <div className="max-w-md w-full text-center space-y-8">
        <div className="flex justify-center">
          <div className="flex justify-center items-center w-[72px] h-[72px] bg-[#FFF4ED] rounded-full">
            <ErrorIcon />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 leading-tight">
          {isTranslationKey(title) ? t(title) : title}
        </h1>

        <p className="text-base text-gray-600 leading-relaxed">
          {isTranslationKey(message) ? t(message) : message}
        </p>

        <div className="pt-4">
          <Button
            variant="primary"
            size="large"
            className="w-full"
            loading={redirecting}
            onClick={onGoBackToMerchantClicked(
              session,
              redirecting,
              setRedirecting,
              sendMobileMessage,
            )}
          >
            {t("error.go_back_to_merchant")}
          </Button>
        </div>
      </div>
    </div>
  );
}
